import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'

import { useIncidentAudio } from './../../features/account-information/hooks/useIncidentAudio'
import { useFilterNotification } from './useFilterNotification'
import { useIncidentsBroadCastChannel } from './useIncidentsBroadCastChannel'
import { useBrowserNotifications } from '@/hooks/useBrowserNotifications'
import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { useDispatch, useSelector } from 'react-redux'

import { ApolloError, useApolloClient, useSubscription } from '@apollo/client'

import { GraphqlClientType } from '@/config/apollo'
import {
  NotificationType,
  ON_UPDATE_NOTIFICATION_COUNT,
  OnUpdateNotificationCountSubscription,
} from '@/graphql/appsync-notifications'
import { IncidentPriority } from '@/graphql/generated/schemas'
import { useAppSelector } from '@/redux/hooks'
import { selectMe } from '@/redux/me/meSlice'
import {
  selectAreNotificationsMuted,
  selectNotifications,
  setNotificationCount,
  setNotifications,
} from '@/redux/notifications/notificationsSlice'
import { trackEvent } from '@/utils/analytics'

import { createSubscription } from './subscription'
import { NotificationDataIProps, UseOnNotificationIProps } from './types'
import { claimNotification, isUniqueNotification } from './utils'

export const NotificationsContext = createContext<UseOnNotificationIProps>(null)

export const NotificationsProvider = ({
  children,
}: {
  children: ReactNode
}) => {
  const dispatch = useDispatch()
  const notifications = useSelector(selectNotifications)
  const areNotificationsMuted = useSelector(selectAreNotificationsMuted)
  const me = useAppSelector(selectMe)
  const isPriorityQueueOn = useFeatureFlag(FeatureFlag.priorityQueue)
  // Timestamps of notifications already handled, shown or not (see claimNotification).
  const handledTimestamps = useRef(new Set<string>())

  const { shouldShowNotification } = useFilterNotification()
  const { postIncidentsMessage } = useIncidentsBroadCastChannel()

  const client = useApolloClient()
  const [shouldReconnect, setShouldReconnect] = useState(true)
  const [error, setError] = useState<ApolloError | undefined>(undefined)
  const [data, setData] = useState<NotificationDataIProps | undefined>(
    undefined
  )

  useEffect(() => {
    if (me?.id) {
      const subscription = createSubscription({
        client: client,
        userId: me.id,
        setData,
        setError,
        setShouldReconnect,
      })
      return () => subscription.unsubscribe()
    }
  }, [shouldReconnect, me?.id])

  const incidentAudio = useIncidentAudio()
  const { showIncidentBanner } = useBrowserNotifications()

  /**
   * @return boolean if a sound was played or not. Depends on user settings, feature flag, and actually playing (browser permissions)
   */
  const playAlertIfApplicable = async (
    priority?: IncidentPriority | null,
    notificationType?: NotificationType
  ): Promise<boolean> => {
    const didPlaySound: boolean = await incidentAudio.playAlertSound(
      priority,
      notificationType
    )

    if (didPlaySound) {
      trackEvent(`Played Incident Audio Alert`, {
        incidentId: data.notification?.incidentId,
        incidentDisplayId: data.notification?.incidentDisplayId,
        incident_priority: priority ?? null,
      })
    }

    return didPlaySound
  }

  useEffect(() => {
    const handleNotification = async () => {
      if (data?.notification) {
        // Add new notification. Check if unique to prevent dups
        if (
          isUniqueNotification(notifications, data) &&
          claimNotification(handledTimestamps.current, data)
        ) {
          // Always refresh incidents table and global count, regardless of mute state
          client.refetchQueries({ include: ['GlobalUnresolvedIncidentsCount'] })
          // Broadcast out each time we have a unique Incident come in
          postIncidentsMessage(data)

          let shouldWeShow = false
          let didPlaySound = false
          let didShowBanner = false

          const priority = data.notification.incidentPriority
          const notificationType = data.notification.notificationType
          // Priority belongs to the incident, not the event, so a Critical incident's
          // clear/flag/audio-message notifications carry the same priority as its
          // arrival — restrict the bypasses to the new-incident event itself, or every
          // later event on that incident would also bypass the table filter and sound.
          const isNewCriticalIncident =
            priority === IncidentPriority.Critical &&
            notificationType === NotificationType.IncidentNew
          // A new Critical incident is shown even if the active table filter would
          // hide it (priorityQueue).
          const isCriticalFilterBypass =
            isNewCriticalIncident && isPriorityQueueOn
          // While the organization's Critical-sound policy is on, a new Critical
          // incident also plays its sound when muted, filtered out, or when the
          // operator turned their own audio alerts off (ENG-35923).
          const isCriticalSoundBypass = incidentAudio.isCriticalSoundBypass(
            priority,
            notificationType
          )

          // Pop-ups and browser banners follow the bell: muted means none, Critical
          // included. Only the sound can bypass the mute, and only under the policy.
          if (!areNotificationsMuted) {
            shouldWeShow =
              isCriticalFilterBypass ||
              (await shouldShowNotification(data.notification.incidentId))
          }

          if (shouldWeShow) {
            dispatch(setNotifications([...notifications, data?.notification]))
          }
          if (shouldWeShow || isCriticalSoundBypass) {
            didPlaySound = await playAlertIfApplicable(
              priority,
              notificationType
            )
          }
          if (shouldWeShow) {
            // Inherits the toast's rules: mute and the active-filter match.
            didShowBanner = showIncidentBanner(data.notification)
          }

          // A new-incident toast MAY have been shown - let's track this
          trackEvent(`Received New Incident Notification`, {
            incidentId: data.notification.incidentId, // Incident UUID. Can get interesting insights into TTR after showing a toast, etc
            notifications_are_muted: areNotificationsMuted, // If bottom right bell/mute icon is clicked
            tried_to_show: shouldWeShow, // If this Incident matches whatever filter?
            did_play_sound: didPlaySound, // If we played an audio alert (can be true while muted, for a new Critical-priority incident under the org policy)
            did_show_browser_banner: didShowBanner, // If we raised an OS-level notification (requires opt-in, permission, and no visible tab)
            type: data.notification.notificationType,
            incident_priority: priority ?? null,
          })
        }
      }
    }
    handleNotification()
  }, [data])

  useSubscription<OnUpdateNotificationCountSubscription>(
    ON_UPDATE_NOTIFICATION_COUNT,
    {
      variables: { userId: me?.id },
      skip: !me?.id,
      onSubscriptionData: ({ subscriptionData }) => {
        dispatch(
          setNotificationCount(
            subscriptionData?.data?.onUpdateNotificationCount.notificationCount
          )
        )
      },
      context: { target: GraphqlClientType.APPSYNC_NOTIFICATIONS },
    }
  )

  return (
    <NotificationsContext.Provider value={{ data, error }}>
      {children}
    </NotificationsContext.Provider>
  )
}

export const useOnNotification = () => useContext(NotificationsContext)
